<template>
  <div class="auth-sidebar-login">
    <section
      class="login component"
      data-component="login-component"
    >
      <div class="form-wrapper">
        <div class="logo-wrapper">
          <div class="logo-fomo icon-fan-logo-black">
            <span class="sr-only">{{ tc('auth.fansMoTitle') }}</span>
          </div>
        </div>
        <login-form>
          <form
            id="login-form"
            slot-scope="{
              serverErrorMsg,
              isValid,
              isLoading,
              grecaptchaReadyCallback,
              grecaptchaReady,
              grecaptchaCallback,
              grecaptchaExecute,
              grecaptchaToken
            }"
            class="form login-form"
          >
            <fieldset>
              <legend>{{ loginComponentData.Title }}</legend>
              <div class="flex-grid-1-col gutter-lg">
                <div class="input">
                  <input
                    id="email"
                    type="text"
                    name="username"
                    value=""
                    class=" req"
                    data-val="true"
                    :data-val-required="getValidationForField('required', 'userIdentifier')"
                    data-clear="true"
                    data-field-type="SingleLineText"
                  >

                  <label
                    for="username"
                    class="req floating"
                  >

                    {{ tc('formFields.userIdentifier') }}

                  </label>

                  <span
                    data-valmsg-for="username"
                    data-valmsg-replace="true"
                    class="field-validation-valid"
                  />
                </div>
                <div class="input">
                  <input
                    id="password"
                    type="password"
                    name="password"
                    value=""
                    autocomplete="current-password"
                    class=" req"
                    data-val="true"
                    :data-val-required="getValidationForField('required', 'password')"
                    data-field-type="PasswordField"
                  >

                  <label
                    for="password"
                    class="req floating"
                  >

                    {{ tc('formFields.password') }}

                  </label>
                  <em
                    :aria-label="tc('formFields.passwordVisiblityToggle')"
                    tabindex="0"
                    class="fa fa-eye password-toggle"
                    type="button"
                    role="switch"
                    aria-checked="false"
                    data-for="password"
                  />

                  <span
                    data-valmsg-for="password"
                    data-valmsg-replace="true"
                    class="field-validation-valid"
                  />
                </div>
              </div>
              <div class="flex-grid-2-col gutter-lg form-options ">
                <ul class="remember-me">
                  <li>
                    <label>
                      <input
                        id="remember_me"
                        name="remember_me"
                        type="checkbox"
                      >
                      {{ tc('auth.rememberMe') }}
                    </label>
                    <span
                      data-valmsg-for="remember_me"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    />
                  </li>
                </ul>
                <div class="input no-label">
                  <a
                    class="forgot-password"
                    :href="loginComponentData.ForgotLink"
                  >{{ tc('auth.forgotPassword') }}</a>
                </div>
              </div>
            </fieldset>

            <template v-if="loginComponentData.ReCaptchaEnabled">
              <grecaptcha
                ref="grecaptchaLogin"
                :sitekey="loginComponentData.ReCaptchaSiteKey"
                :element-id="'g-recaptcha'"
                @grecaptcha-callback="grecaptchaCallback"
                @grecaptcha-ready="grecaptchaReadyCallback"
              />
              <input
                v-model="grecaptchaToken"
                type="hidden"
                name="grecaptchaToken"
              >
            </template>

            <div class="form-submit">
              <button
                v-if="loginComponentData.ReCaptchaEnabled"
                class="button login-cta"
                :class="[{ loading: isLoading }]"
                :disabled="!isValid || !grecaptchaReady"
                @click.prevent="grecaptchaExecute"
              >
                {{ tc('auth.signIn') }}
              </button>
              <button
                v-else
                class="button login-cta"
                :class="[{ loading: isLoading }]"
                :disabled="!isValid"
              >
                {{ tc('auth.signIn') }}
              </button>
              <div
                class="sr-only"
                role="status"
              >
                {{ isLoading ? tc('accessibility.loadingButtonStatus') : '' }}
              </div>
            </div>

            <div
              class="error-msg"
              :class="[{ show: !!serverErrorMsg }]"
              v-html="serverErrorMsg"
            />
          </form>
        </login-form>
      </div>
    </section>
    <section
      class="join-fans component"
      data-component="join-fans-component"
    >
      <div class="join-fans-wrapper">
        <div class="join-fans-title">
          {{ fansJoinComponentData.Title }}
        </div>
        <p class="join-fans-description">
          {{ fansJoinComponentData.SubTitle }}
        </p>
        <ul class="benefits-list">
          <li
            v-for="benefit in fansJoinComponentData.Benefits"
            class="benefits-list-item"
          >
            <span
              class="icon"
              :style="`background-image: url('${benefit.IconUrl}')`"
            />
            <div>{{ benefit.Description }}</div>
          </li>
        </ul>
        <div>
          <div class="cta-wrapper">
            <a
              href="#"
              class="cta-join-now"
              @click.prevent="$emit('change-component', 'registration')"
            >
              {{ fansJoinComponentData.JoinNowLabel }}
            </a>
            <a
              :href="fansJoinComponentData.LearnMoreLink"
              target="_blank"
              class="cta-learn-more"
            >
              {{ fansJoinComponentData.LearnMoreLabel }}
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import Grecaptcha from '@components/02.account/grecaptcha/grecaptcha.vue'

  export default {
    name: 'SidebarLogin',
    components: {
      Grecaptcha
    },
    mixins: [Tracking],
    inject: {
      skipBookingTracking: {
        default: false
      }
    },
    computed: {
      ...mapState('auth', ['loginComponentData', 'fansJoinComponentData']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('booking', ['getQuery', 'bookingFlow']),
    },
    mounted() {
      if (!this.skipBookingTracking) {
        this.$track(this.getTrackingData('booking', this.getQuery, { event: 'step4' }))

        this.$track(this.getTrackingData(this.isBookingEditMode ? 'modifyReservation' : 'booking', this.getQuery, {
          event: 'bookingStateChange',
          bookingEngine: {
            bookingStepNumber: this.isBookingEditMode ? '2.2.1' : '3.2.1',
            bookingStepName: 'Fans of MO Login Flyout',
            bookingEngineVersion: this.isBookingEditMode ? '2' : '1',
            bookingFlow: this.bookingFlow()
          }
        }))
      }
    },
    methods: {
      getValidationForField(validation, field = '') {
        return this.tc(`validations.${validation}`).replace('{0}', this.tc(`formFields.${field}`))
      }
    },
  }
</script>
